import type { Token } from '@/domain/types/Token'
import type { VaultMarket } from '@/features/evault-details/data/products'
import type { YieldSource } from '@/features/unified-earn/types'
import type { Address } from 'viem'

export type BorrowSource = YieldSource | 'usdxl'

/** Rate curve model type — determines how we compute rate sensitivity */
export type RateModelType = 'aave-kink' | 'linear-approx'

/** Parameters sufficient to compute rate at any utilization for Aave v3 kink model */
export interface AaveKinkParams {
  type: 'aave-kink'
  baseRate: number // 0-1 scale (e.g. 0.01 = 1%)
  slope1: number // 0-1 scale
  slope2: number // 0-1 scale
  optimalUtilization: number // 0-1 scale
  reserveFactor: number // 0-1 scale
}

/** Fallback: linear approximation from current rate + utilization */
export interface LinearApproxParams {
  type: 'linear-approx'
  currentRate: number // 0-100 scale (the APY)
  currentUtilization: number // 0-1 scale
}

export type RateModelParams = AaveKinkParams | LinearApproxParams

export interface LiquidityInfo {
  /** Total supply in the market (USD) */
  totalSupplyUsd: number
  /** Total borrow in the market (USD) */
  totalBorrowUsd: number
  /** Available to borrow (USD) — may be capped */
  availableUsd: number
  /** Current utilization 0-1 */
  utilization: number
  /** Rate model for computing impact */
  rateModel: RateModelParams
}

/** Rates at a particular trade size step */
export interface DeltaStep {
  /** Fraction of max trade (0.25, 0.50, 0.75, 1.0) */
  pct: number
  /** Trade size in USD at this step */
  tradeUsd: number
  /** Borrow APY after this trade (0-100) */
  borrowAPY: number
  /** Deposit APY after this trade (0-100) */
  depositAPY: number
  /** Spread at this step (0-100) */
  spread: number
  /** Borrow rate change from model baseline (not from displayed rate) */
  borrowDelta: number
  /** Deposit rate change from model baseline (not from displayed rate) */
  depositDelta: number
}

export interface BorrowOpportunity {
  source: BorrowSource
  address: Address
  asset: string
  token?: Token
  borrowAPY: number // 0-100 scale
  name: string
  liquidity?: LiquidityInfo
  /** Market classification (Prime, Yield, etc.) — used for elend label */
  market?: VaultMarket
  /** For isolated pairs: "COLLATERAL/BORROW" (e.g. "HYPE/USDC") */
  pairName?: string
}

export interface DepositOpportunity {
  source: YieldSource
  address: Address
  asset: string
  token?: Token
  depositAPY: number // 0-100 scale
  name: string
  liquidity?: LiquidityInfo
  /** Market classification (Prime, Yield, etc.) — used for elend label */
  market?: VaultMarket
  /** For isolated pairs: "COLLATERAL/BORROW" (e.g. "HYPE/USDC") */
  pairName?: string
}

export interface ArbitrageOpportunity {
  id: string
  borrow: BorrowOpportunity
  deposit: DepositOpportunity
  /** Current spread: depositAPY - borrowAPY (0-100 scale) */
  spread: number
  /** The common asset group being arbitraged */
  asset: string
  /** Max trade size in USD before spread collapses to zero */
  maxTradeUsd: number | undefined
  /** Borrow rate after executing maxTradeUsd (0-100 scale) */
  borrowAPYAtMax: number | undefined
  /** Deposit rate after executing maxTradeUsd (0-100 scale) */
  depositAPYAtMax: number | undefined
  /** Spread at max trade (0-100 scale) */
  spreadAtMax: number | undefined
  /** Rate deltas at 25%, 50%, 75%, 100% of max trade */
  deltaSteps: DeltaStep[]
}

export type OpportunitySourceFilter = 'all' | BorrowSource
export type OpportunitySortField = 'spread' | 'maxTrade' | 'borrowAPY' | 'depositAPY'

const MIN_VIABLE_SPREAD = 1 // percent

export function hasViableSpread(opp: ArbitrageOpportunity): boolean {
  return opp.deltaSteps.length === 0 || opp.deltaSteps.some((step) => step.spread >= MIN_VIABLE_SPREAD)
}
